// Quick-add clinical note modal for the dashboard. No Bedrock — direct save via
// POST /notes. The user's explicit Save action is the confirmation.

import { useState } from "react";
import { createNote, ApiError } from "../lib/api.js";
import { FileTextIcon } from "./icons.js";
import { ModalShell } from "./ModalShell.js";
import { ClearableTextarea } from "./ClearableInput.js";

function todayStr() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

export function QuickNoteLog({
  patientId,
  onClose,
  onSaved,
}: {
  patientId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [noteText, setNoteText] = useState("");
  const [timeMode, setTimeMode] = useState<"now" | "custom">("now");
  const [customDate, setCustomDate] = useState(todayStr);
  const [customTime, setCustomTime] = useState(() => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function selectCustomMode() {
    const n = new Date();
    setCustomTime(`${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`);
    setCustomDate(todayStr());
    setTimeMode("custom");
  }

  async function save() {
    const text = noteText.trim();
    if (!text) {
      setError("Note text is required");
      return;
    }
    let recordedAt: string | undefined;
    if (timeMode === "custom" && customTime) {
      const [year, month, day] = customDate.split("-").map(Number);
      const [h, m] = customTime.split(":").map(Number);
      recordedAt = new Date(year!, month! - 1, day!, h ?? 0, m ?? 0, 0, 0).toISOString();
    }
    setBusy(true);
    setError(null);
    try {
      await createNote(patientId, text, undefined, recordedAt);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save note");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Add a note"
      icon={<FileTextIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
    >
        <div className="space-y-4">
          {error && (
            <p className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </p>
          )}

          {/* Note text area */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Note</label>
            <ClearableTextarea
              autoFocus
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your clinical note…"
              rows={5}
              className="w-full resize-none rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-(--accent)/50"
            />
          </div>

          {/* Time */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Time</label>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex rounded-full bg-gray-100 dark:bg-gray-800 p-0.5 text-xs font-medium">
                <button
                  onClick={() => setTimeMode("now")}
                  className={
                    "rounded-full px-4 py-1 transition-all " +
                    (timeMode === "now"
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400")
                  }
                >
                  Now
                </button>
                <button
                  onClick={selectCustomMode}
                  className={
                    "rounded-full px-4 py-1 transition-all " +
                    (timeMode === "custom"
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400")
                  }
                >
                  Custom
                </button>
              </div>
              {timeMode === "custom" && (
                <>
                  <input
                    type="date"
                    value={customDate}
                    max={todayStr()}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                  />
                  <input
                    type="time"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                    className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                  />
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => void save()}
              disabled={busy || !noteText.trim()}
              className="flex-1 rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
    </ModalShell>
  );
}
